import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PageHeader } from '../../components/ui/PageHeader';
import { ReportPageLinks } from '../../components/report/ReportPageLinks';
import { Modal } from '../../components/ui/Modal';
import {
  fetchInventoryIcreCustomers,
  fetchInventoryList,
  formatInventoryDate,
  formatInventoryDisplayIpg,
  formatInventoryDisplayItemNo,
  formatInventoryQty,
  formatInventoryTa,
  formatInventoryWhole,
  joinInventoryCustomerPatterns,
} from '../../lib/inventoryItems';
import {
  CLEARANCE_TA_MONTHS,
  INVENTORY_ANALYSIS_TABS,
  isClearanceRow,
  isLowTa,
  LOW_TA_MONTHS,
} from '../../lib/inventoryAnalysisConfig';
import { exportInventoryAnalysisExcel } from '../../lib/inventoryAnalysisExport';
import type { InventoryItem, InventoryIpgTab, InventorySort } from '../../types/inventory';

/** Load full filtered list in one request (RPC allows up to 10_000). */
const PAGE_SIZE = 10000;

const SORT_OPTIONS: { label: string; value: InventorySort }[] = [
  { label: 'Customer A–Z', value: 'customer_asc' },
  { label: 'Inventory ↓', value: 'inventory_desc' },
  { label: 'Inventory ↑', value: 'inventory_asc' },
  { label: 'Item A–Z', value: 'item_no_asc' },
  { label: 'Item Z–A', value: 'item_no_desc' },
  { label: '1st ETA ↑', value: 'ship1_eta_asc' },
  { label: 'TA ↑', value: 'ta_asc' },
  { label: 'TA ↓', value: 'ta_desc' },
  { label: 'BPO ↓', value: 'bpo_desc' },
  { label: 'FSM ↓', value: 'fsm_desc' },
];

type CustomerGroup = {
  customer_no: string;
  customer_name: string;
  salesperson_code: string;
  rows: InventoryItem[];
};

function groupByCustomer(rows: InventoryItem[]): CustomerGroup[] {
  const map = new Map<string, CustomerGroup>();
  for (const row of rows) {
    const key = row.customer_no || '—';
    const existing = map.get(key);
    if (existing) {
      existing.rows.push(row);
      if (!existing.salesperson_code && row.salesperson_code) {
        existing.salesperson_code = row.salesperson_code;
      }
    } else {
      map.set(key, {
        customer_no: key,
        customer_name: row.customer_name || key,
        salesperson_code: row.salesperson_code || '',
        rows: [row],
      });
    }
  }
  return [...map.values()];
}

export function InventoryListPage() {
  const [tab, setTab] = useState<InventoryIpgTab>('FG-FOCUS');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sort, setSort] = useState<InventorySort>('customer_asc');
  const [optionsOpen, setOptionsOpen] = useState(false);
  /** null = waiting for ICRE so we can default to select-all. */
  const [customers, setCustomers] = useState<string | null>(null);
  const [customersDraft, setCustomersDraft] = useState('');
  const [icreOptions, setIcreOptions] = useState<string[]>([]);
  const [icreFilter, setIcreFilter] = useState('');
  const [icreLoading, setIcreLoading] = useState(true);
  const [icreError, setIcreError] = useState<string | null>(null);
  const [rows, setRows] = useState<InventoryItem[]>([]);
  const [total, setTotal] = useState(0);
  const [usageLabels, setUsageLabels] = useState<string[]>(['M-3', 'M-2', 'M-1', 'M']);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [exporting, setExporting] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportMode, setExportMode] = useState<'current' | 'by_sp'>('current');
  const [salespersonOptions, setSalespersonOptions] = useState<string[]>([]);
  const [exportSalespersonCodes, setExportSalespersonCodes] = useState<string[]>([]);
  const [spOptionsLoading, setSpOptionsLoading] = useState(false);
  const requestId = useRef(0);
  const icreInitRef = useRef(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  /** Load ICRE options; first success defaults the report to select-all reportable customers. */
  const loadIcreOptions = useCallback(async () => {
    setIcreLoading(true);
    setIcreError(null);
    try {
      const codes = await fetchInventoryIcreCustomers();
      setIcreOptions(codes);
      if (codes.length === 0) {
        setIcreError('No Customer cross-reference numbers found in ICRE.');
      }
      if (!icreInitRef.current) {
        icreInitRef.current = true;
        const all = joinInventoryCustomerPatterns(codes);
        setCustomers(all);
        setCustomersDraft(all);
      }
    } catch (err) {
      setIcreOptions([]);
      setIcreError(
        err instanceof Error ? err.message : 'Failed to load ICRE customers.',
      );
      if (!icreInitRef.current) {
        icreInitRef.current = true;
        setCustomers('');
        setCustomersDraft('');
      }
    } finally {
      setIcreLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadIcreOptions();
  }, [loadIcreOptions]);

  const selectedIcreCodes = useMemo(() => {
    const selected = new Set<string>();
    for (const part of customersDraft.split('|')) {
      // Strip trailing * so Y0175* still checks the Y0175 box.
      const code = part.trim().replace(/\*+$/g, '');
      if (code) selected.add(code);
    }
    return selected;
  }, [customersDraft]);

  const filteredIcreOptions = useMemo(() => {
    const q = icreFilter.trim().toLowerCase();
    if (!q) return icreOptions;
    return icreOptions.filter((code) => code.toLowerCase().includes(q));
  }, [icreFilter, icreOptions]);

  const toggleIcreCode = (code: string) => {
    const next = new Set(selectedIcreCodes);
    if (next.has(code)) next.delete(code);
    else next.add(code);
    const ordered = icreOptions.filter((c) => next.has(c));
    setCustomersDraft(joinInventoryCustomerPatterns(ordered));
  };

  const selectAllIcre = () => {
    setCustomersDraft(joinInventoryCustomerPatterns(icreOptions));
  };

  const clearIcreSelection = () => {
    setCustomersDraft('');
  };

  const load = useCallback(async () => {
    if (customers === null) return;
    const id = ++requestId.current;
    setLoading(true);
    setError(null);
    try {
      const tabDef = INVENTORY_ANALYSIS_TABS.find((t) => t.id === tab) ?? INVENTORY_ANALYSIS_TABS[0];
      const result = await fetchInventoryList({
        ipg: tabDef.ipg,
        search: debouncedSearch,
        page: 1,
        pageSize: PAGE_SIZE,
        sort,
        customers,
        // Always today — RPC falls back to CURRENT_DATE when omitted.
      });
      if (id !== requestId.current) return;
      const items = tabDef.clearanceOnly
        ? result.items.filter((row) => isClearanceRow(row))
        : result.items;
      setRows(items);
      setTotal(tabDef.clearanceOnly ? items.length : result.meta.total);
      if (result.meta.usage_labels?.length === 4) {
        setUsageLabels(result.meta.usage_labels);
      }
    } catch (err) {
      if (id !== requestId.current) return;
      setRows([]);
      setTotal(0);
      setError(err instanceof Error ? err.message : 'Failed to load inventory.');
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [tab, debouncedSearch, sort, customers]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Salesperson list for export-by-SP — load when that mode is chosen. */
  useEffect(() => {
    if (!exportOpen || exportMode !== 'by_sp' || customers === null) return;
    let cancelled = false;
    setSpOptionsLoading(true);
    void fetchInventoryList({
      ipg: 'FG-FOCUS',
      page: 1,
      pageSize: PAGE_SIZE,
      sort: 'customer_asc',
      customers,
    })
      .then((result) => {
        if (cancelled) return;
        const codes = [
          ...new Set(
            result.items
              .map((row) => (row.salesperson_code || '').trim())
              .filter(Boolean),
          ),
        ].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
        setSalespersonOptions(codes);
        setExportSalespersonCodes((prev) => prev.filter((c) => codes.includes(c)));
      })
      .catch(() => {
        if (!cancelled) setSalespersonOptions([]);
      })
      .finally(() => {
        if (!cancelled) setSpOptionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [exportOpen, exportMode, customers]);

  const groups = useMemo(() => groupByCustomer(rows), [rows]);
  const colCount = 21;
  const appliedCustomers = customers ?? '';
  const patternCount = appliedCustomers.split('|').filter((p) => p.trim()).length;
  const truncated = total > rows.length;

  const openOptions = () => {
    setCustomersDraft(appliedCustomers);
    setIcreFilter('');
    setOptionsOpen(true);
  };

  const closeOptions = () => {
    setCustomersDraft(appliedCustomers);
    setIcreFilter('');
    setOptionsOpen(false);
  };

  const applyOptions = () => {
    setCustomers(customersDraft.trim());
    setOptionsOpen(false);
  };

  const toggleCustomer = (customerNo: string) => {
    setCollapsed((prev) => ({ ...prev, [customerNo]: !prev[customerNo] }));
  };

  const expandAll = () => setCollapsed({});
  const collapseAll = () => {
    const next: Record<string, boolean> = {};
    for (const g of groups) next[g.customer_no] = true;
    setCollapsed(next);
  };

  const allCollapsed =
    groups.length > 0 && groups.every((g) => Boolean(collapsed[g.customer_no]));
  const toggleCustomersExpanded = () => {
    if (allCollapsed) expandAll();
    else collapseAll();
  };

  const toggleExportSalesperson = (code: string) => {
    setExportSalespersonCodes((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
    );
  };

  const selectAllExportSalespersons = () => {
    setExportSalespersonCodes([...salespersonOptions]);
  };

  const clearExportSalespersons = () => {
    setExportSalespersonCodes([]);
  };

  const openExport = () => {
    setExportMode('current');
    setExportOpen(true);
  };

  const closeExport = () => {
    if (exporting) return;
    setExportOpen(false);
  };

  const runExport = () => {
    setExporting(true);
    void exportInventoryAnalysisExcel({
      customers: appliedCustomers,
      search: debouncedSearch,
      sort,
      salespersonCodes: exportMode === 'by_sp' ? exportSalespersonCodes : [],
    })
      .then(() => setExportOpen(false))
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Export failed.');
      })
      .finally(() => setExporting(false));
  };

  return (
    <div className="inventory-page">
      <PageHeader
        title="Customise Items Analysis & Review"
        actions={<ReportPageLinks sibling={{ slug: 'open-po', label: 'Open PO' }} />}
      />

      <div className="inventory-toolbar">
        <div className="inventory-toolbar__filters">
          <select
            className="inventory-inline-view"
            value={tab}
            onChange={(e) => setTab(e.target.value as InventoryIpgTab)}
            aria-label="View"
          >
            {INVENTORY_ANALYSIS_TABS.map((item) => (
              <option key={item.id} value={item.id}>
                {item.label}
              </option>
            ))}
          </select>
          <select
            className="inventory-inline-sort"
            value={SORT_OPTIONS.find((opt) => opt.value === sort)?.label ?? SORT_OPTIONS[0].label}
            onChange={(e) => {
              const next = SORT_OPTIONS.find((opt) => opt.label === e.target.value);
              if (next) setSort(next.value);
            }}
            aria-label="Sort"
          >
            {SORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.label}>
                {opt.label}
              </option>
            ))}
          </select>
          <p className="inventory-page__meta">
            {loading && rows.length === 0
              ? 'Loading…'
              : `${loading ? 'Updating… · ' : ''}${
                  truncated
                    ? `Showing ${rows.length.toLocaleString('en-SG')} of ${total.toLocaleString('en-SG')} rows`
                    : `${total.toLocaleString('en-SG')} row${total === 1 ? '' : 's'}`
                }`}
          </p>
          <p className="inventory-page__legend" aria-label="Color code">
            <span className="inventory-page__legend-item inventory-page__legend-item--low">
              Red: BPO TA ≤ {LOW_TA_MONTHS} mo
            </span>
            <span className="inventory-page__legend-item inventory-page__legend-item--clearance">
              Yellow: BPO TA ≥ {CLEARANCE_TA_MONTHS} mo / OBR / OBX
            </span>
          </p>
        </div>
        <div className="inventory-toolbar__actions">
          <div className="inventory-inline-search">
            <input
              className="inventory-inline-search__input"
              type="text"
              inputMode="search"
              autoComplete="off"
              placeholder="Search customer, item, description or IPG"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search"
            />
          </div>
          <button
            type="button"
            className="btn-secondary btn-sm"
            onClick={openOptions}
            aria-haspopup="dialog"
            aria-expanded={optionsOpen}
            disabled={customers === null}
          >
            Options
            {patternCount > 0 ? (
              <span className="inventory-toolbar__badge">{patternCount}</span>
            ) : null}
          </button>
          <button
            type="button"
            className="btn-secondary btn-sm"
            onClick={openExport}
            aria-haspopup="dialog"
            aria-expanded={exportOpen}
            disabled={exporting || loading || customers === null}
          >
            Export
          </button>
          <button
            type="button"
            className="btn-secondary btn-sm"
            onClick={toggleCustomersExpanded}
            disabled={groups.length === 0}
          >
            {allCollapsed ? 'Expand' : 'Collapse'}
          </button>
        </div>
      </div>

      <Modal title="Export" open={exportOpen} onClose={closeExport} compactHeader>
        <div className="inventory-export-modal">
          <fieldset className="inventory-export-modal__modes">
            <legend className="inventory-export-modal__legend">What to export</legend>
            <label className="inventory-export-modal__mode">
              <input
                type="radio"
                name="inventory-export-mode"
                checked={exportMode === 'current'}
                onChange={() => setExportMode('current')}
                disabled={exporting}
              />
              <span>
                <strong>Current filters</strong>
                <span className="inventory-export-modal__mode-hint">
                  Same customers / search as this page — all salespersons
                </span>
              </span>
            </label>
            <label className="inventory-export-modal__mode">
              <input
                type="radio"
                name="inventory-export-mode"
                checked={exportMode === 'by_sp'}
                onChange={() => setExportMode('by_sp')}
                disabled={exporting}
              />
              <span>
                <strong>By salesperson code</strong>
                <span className="inventory-export-modal__mode-hint">
                  Filter the Excel file by selected sales codes (does not change the table)
                </span>
              </span>
            </label>
          </fieldset>

          {exportMode === 'by_sp' ? (
            <div className="inventory-export-modal__sp">
              <div className="inventory-export-modal__sp-actions">
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  onClick={selectAllExportSalespersons}
                  disabled={exporting || spOptionsLoading || salespersonOptions.length === 0}
                >
                  Select all
                </button>
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  onClick={clearExportSalespersons}
                  disabled={exporting || exportSalespersonCodes.length === 0}
                >
                  Clear (all)
                </button>
              </div>
              {spOptionsLoading ? (
                <p className="inventory-export-modal__hint">Loading salespersons…</p>
              ) : salespersonOptions.length === 0 ? (
                <p className="inventory-export-modal__hint">
                  No salesperson codes in the current customer filter.
                </p>
              ) : (
                <ul className="inventory-export-modal__sp-list" aria-label="Salesperson codes">
                  {salespersonOptions.map((code) => (
                    <li key={code}>
                      <label className="inventory-export-modal__sp-option">
                        <input
                          type="checkbox"
                          checked={exportSalespersonCodes.includes(code)}
                          onChange={() => toggleExportSalesperson(code)}
                          disabled={exporting}
                        />
                        <span className="mono">{code}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              )}
              <p className="inventory-export-modal__hint">
                Leave empty to export all salespersons under the current customer filter.
              </p>
            </div>
          ) : null}

          <div className="inventory-export-modal__footer">
            <button
              type="button"
              className="btn-secondary btn-sm"
              onClick={closeExport}
              disabled={exporting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary btn-sm"
              onClick={runExport}
              disabled={exporting}
            >
              {exporting ? 'Exporting…' : 'Export'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal title="Options" open={optionsOpen} onClose={closeOptions} wide compactHeader>
        <div className="inventory-options inventory-options--modal">
          <div className="inventory-options__field inventory-options__field--wide">
            <label className="inventory-options__label" htmlFor="inventory-icre-filter">
              Customer No.
            </label>
            <p className="inventory-options__sublabel">Reportable ICRE customers from PowerBI</p>
            <div className="inventory-icre-picker">
              <input
                id="inventory-icre-filter"
                type="text"
                inputMode="search"
                autoComplete="off"
                className="inventory-icre-picker__search"
                placeholder="Filter customer nos…"
                value={icreFilter}
                onChange={(e) => setIcreFilter(e.target.value)}
                disabled={icreLoading || icreOptions.length === 0}
                aria-label="Filter ICRE customer numbers"
              />
              {icreLoading ? (
                <p className="inventory-icre-picker__status">Loading customers from PowerBI…</p>
              ) : icreError ? (
                <p className="inventory-icre-picker__status inventory-icre-picker__status--error">
                  {icreError}
                </p>
              ) : icreOptions.length === 0 ? (
                <p className="inventory-icre-picker__status">No customer numbers found.</p>
              ) : (
                <ul className="inventory-icre-picker__list" aria-label="ICRE customer numbers">
                  {filteredIcreOptions.map((code) => (
                    <li key={code}>
                      <label className="inventory-icre-picker__option">
                        <input
                          type="checkbox"
                          checked={selectedIcreCodes.has(code)}
                          onChange={() => toggleIcreCode(code)}
                        />
                        <span className="mono">{code}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              )}
              <p className="inventory-icre-picker__meta">
                {icreLoading
                  ? '…'
                  : `${selectedIcreCodes.size.toLocaleString()} selected · ${icreOptions.length.toLocaleString()} reportable`}
                {icreFilter.trim() && filteredIcreOptions.length !== icreOptions.length
                  ? ` · showing ${filteredIcreOptions.length.toLocaleString()}`
                  : ''}
              </p>
            </div>
          </div>

          <div className="inventory-options__tips" aria-label="Notes">
            <p className="inventory-options__tips-title">Notes</p>
            <ul className="inventory-options__tips-list">
              <li>
                Each tick applies as a wildcard (e.g. <code>Y0175*</code>) so ship-to accounts are
                included.
              </li>
              <li>Clear selection = all customers. Apply to refresh the report.</li>
              <li>
                List omits FG-OBR / FG-OBX with inventory 0 (nothing to show on any view).
              </li>
              <li>
                Red: BPO TA ≤ {LOW_TA_MONTHS} mo. Yellow: BPO TA ≥ {CLEARANCE_TA_MONTHS} mo or
                FG-OBR / FG-OBX.
              </li>
            </ul>
          </div>

          <div className="inventory-options__footer">
            <button
              type="button"
              className="btn-secondary btn-sm"
              onClick={selectAllIcre}
              disabled={icreLoading || icreOptions.length === 0}
            >
              Select all
            </button>
            <button
              type="button"
              className="btn-secondary btn-sm"
              onClick={clearIcreSelection}
              disabled={selectedIcreCodes.size === 0 && !customersDraft.trim()}
            >
              Clear
            </button>
            <button type="button" className="btn-primary btn-sm" onClick={applyOptions}>
              Apply
            </button>
          </div>
        </div>
      </Modal>

      {error ? <p className="inventory-page__error">{error}</p> : null}

      <div className="inventory-table-frame">
        <div className="inventory-table-wrap">
          <table className="inventory-table inventory-table--wide inventory-table--analysis">
            <thead>
              <tr className="inventory-table__group">
                <th className="inventory-col--ipg inventory-col--core" />
                <th className="inventory-col--item inventory-col--core" />
                <th colSpan={5} className="inventory-col--core" />
                <th colSpan={2} className="inventory-col--bpo">
                  BPO
                </th>
                <th colSpan={4} className="inventory-col--ship1">
                  First Shipment
                </th>
                <th colSpan={4} className="inventory-col--ship2">
                  Second Shipment
                </th>
                <th colSpan={4} className="inventory-col--usage">
                  Monthly Usage
                </th>
              </tr>
              <tr>
                <th className="inventory-col--ipg inventory-col--core">IPG</th>
                <th className="inventory-col--item inventory-col--core">Item Code</th>
                <th className="inventory-col--core">Description</th>
                <th className="num inventory-col--core">Inventory</th>
                <th className="num inventory-col--core">FS</th>
                <th className="num inventory-col--core">FSM</th>
                <th className="num inventory-col--core inventory-col--ta">TA</th>
                <th className="num inventory-col--bpo">Qty</th>
                <th className="num inventory-col--bpo inventory-col--ta">TA</th>
                <th className="inventory-col--ship1">ETA</th>
                <th className="num inventory-col--ship1">Qty</th>
                <th className="num inventory-col--ship1 inventory-col--ta">TA</th>
                <th className="inventory-col--ship1">Bring Forward?</th>
                <th className="inventory-col--ship2">ETA</th>
                <th className="num inventory-col--ship2">Qty</th>
                <th className="num inventory-col--ship2 inventory-col--ta">TA</th>
                <th className="inventory-col--ship2">Bring Forward?</th>
                <th className="num inventory-col--usage">{usageLabels[0] ?? 'M1'}</th>
                <th className="num inventory-col--usage">{usageLabels[1] ?? 'M2'}</th>
                <th className="num inventory-col--usage">{usageLabels[2] ?? 'M3'}</th>
                <th className="num inventory-col--usage">{usageLabels[3] ?? 'M4'}</th>
              </tr>
            </thead>
            <tbody>
              {!loading && rows.length === 0 ? (
                <tr>
                  <td colSpan={colCount} className="inventory-table__empty">
                    No items match this view.
                  </td>
                </tr>
              ) : (
                groups.map((group) => {
                  const isCollapsed = Boolean(collapsed[group.customer_no]);
                  return (
                    <CustomerGroupRows
                      key={group.customer_no}
                      group={group}
                      collapsed={isCollapsed}
                      onToggle={() => toggleCustomer(group.customer_no)}
                      colCount={colCount}
                    />
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function CustomerGroupRows({
  group,
  collapsed,
  onToggle,
  colCount,
}: {
  group: CustomerGroup;
  collapsed: boolean;
  onToggle: () => void;
  colCount: number;
}) {
  return (
    <>
      <tr className="inventory-table__customer">
        <td colSpan={colCount}>
          <div className="inventory-table__customer-sticky">
            <button type="button" className="inventory-table__customer-btn" onClick={onToggle}>
              <span aria-hidden>{collapsed ? '▸' : '▾'}</span>
              {group.salesperson_code ? (
                <span className="mono inventory-table__customer-sp">{group.salesperson_code}</span>
              ) : null}
              <span className="mono">{group.customer_no}</span>
              <span>{group.customer_name}</span>
              <span className="inventory-table__customer-count">
                {group.rows.length} item{group.rows.length === 1 ? '' : 's'}
              </span>
            </button>
          </div>
        </td>
      </tr>
      {collapsed
        ? null
        : group.rows.map((row) => {
            const lowTa = isLowTa(row.ta, row.ipg);
            const clearanceTa = isClearanceRow(row);
            const className = [
              row.blocked ? 'is-blocked' : '',
              lowTa ? 'is-low-ta' : '',
              clearanceTa ? 'is-clearance-ta' : '',
            ]
              .filter(Boolean)
              .join(' ');
            return (
              <tr key={`${row.customer_no}::${row.item_no}`} className={className || undefined}>
                <td className="inventory-col--ipg inventory-col--core">
                  {formatInventoryDisplayIpg(row.ipg, row.item_no)}
                </td>
                <td className="mono inventory-col--item inventory-col--core">
                  {formatInventoryDisplayItemNo(row.item_no)}
                </td>
                <td className="inventory-col--core">{row.description}</td>
                <td className="num inventory-col--core">{formatInventoryQty(row.inventory)}</td>
                <td className="num inventory-col--core">{formatInventoryWhole(row.fs)}</td>
                <td className="num inventory-col--core">{formatInventoryQty(row.fsm)}</td>
                <td className="num inventory-col--core inventory-col--ta">
                  {formatInventoryTa(row.turn_around)}
                </td>
                <td className="num inventory-col--bpo">{formatInventoryQty(row.bpo)}</td>
                <td
                  className={[
                    'num inventory-col--bpo inventory-col--ta',
                    clearanceTa ? 'inventory-col--clearance-ta' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {formatInventoryTa(row.ta)}
                </td>
                <td className="inventory-col--ship1">{formatInventoryDate(row.ship1_eta)}</td>
                <td className="num inventory-col--ship1">{formatInventoryQty(row.ship1_qty)}</td>
                <td className="num inventory-col--ship1 inventory-col--ta">
                  {formatInventoryTa(row.ship1_ta)}
                </td>
                <td className="inventory-col--ship1">{row.bring_forward || '—'}</td>
                <td className="inventory-col--ship2">{formatInventoryDate(row.ship2_eta)}</td>
                <td className="num inventory-col--ship2">{formatInventoryQty(row.ship2_qty)}</td>
                <td className="num inventory-col--ship2 inventory-col--ta">
                  {formatInventoryTa(row.ship2_ta)}
                </td>
                <td className="inventory-col--ship2">{row.bring_forward_2 || '—'}</td>
                <td className="num inventory-col--usage">{formatInventoryQty(row.usage_m1)}</td>
                <td className="num inventory-col--usage">{formatInventoryQty(row.usage_m2)}</td>
                <td className="num inventory-col--usage">{formatInventoryQty(row.usage_m3)}</td>
                <td className="num inventory-col--usage">{formatInventoryQty(row.usage_m4)}</td>
              </tr>
            );
          })}
    </>
  );
}
